import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, Validators, type UntypedFormGroup } from '@angular/forms';

import { Observable, of, Subscription, take } from 'rxjs';

import { SelectOption } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces/gtm.interface';
import { EMAIL_PATTERN, NAME_PATTERN, rangeBetween, rangeFrom } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { ButtonMode, ButtonSize, ButtonType } from '@fcom/ui-components/components/buttons/button/enums';
import { stripSpaces } from '@fcom/ui-components/utils/string.utils';
import { AnalyticsFormStatus } from '@fcom/service-forms';

import { DarksiteCaseResponse, IncidentUpdatesService } from '../../../../services/darksite/incident-updates.service';
import {
  DarksiteFormControl,
  DarksiteFormData,
  DARKSITE_GTMCONTEXT,
  DOBFormControl,
  PHONE_NUMBER_PATTERN,
  RelationQuestion,
  RelativeQuestion,
} from '../../../../interfaces/darksite.interface';

@Component({
  selector: 'fin-darksite-form',
  templateUrl: './darksite-form.component.html',
  styleUrl: './darksite-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DarksiteFormComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private incidentUpdatesService = inject(IncidentUpdatesService);
  private gtmService = inject(GtmService);
  private subscription: Subscription = new Subscription();

  readonly ButtonType = ButtonType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly DOBFormControl = DOBFormControl;
  readonly DarksiteFormControl = DarksiteFormControl;
  readonly RelationQuestion = RelationQuestion;
  readonly RelativeQuestion = RelativeQuestion;
  readonly textareaLimit30 = 30;

  availableDays$: Observable<SelectOption[]> = of(
    rangeFrom(1, 31).map((day): SelectOption => ({ name: String(day), value: String(day) }))
  );
  availableMonths$: Observable<SelectOption[]> = of(
    rangeFrom(1, 12).map((month): SelectOption => ({ name: String(month), value: String(month) }))
  );
  availableYears$: Observable<SelectOption[]> = of(
    rangeBetween(LocalDate.now().minusYears(100).localYear, LocalDate.now().localYear)
      .reverse()
      .map((year): SelectOption => ({ name: String(year), value: String(year) }))
  );

  formEnds: WritableSignal<boolean> = signal<boolean>(false);
  loading: WritableSignal<boolean> = signal<boolean>(false);
  reactiveForm: UntypedFormGroup;

  get selectedRelative(): RelativeQuestion {
    return this.reactiveForm.controls[DarksiteFormControl.RELATIVE].value ?? null;
  }

  get SelectedRelation(): RelationQuestion {
    return this.reactiveForm.controls[DarksiteFormControl.RELATION].value ?? null;
  }

  get passengerDOB(): UntypedFormGroup {
    return this.reactiveForm.get([DarksiteFormControl.PAX_DOB]) as UntypedFormGroup;
  }

  ngOnInit(): void {
    this.subscription.add(this.createForm());
    this.formEnds = this.incidentUpdatesService.formEnds;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.reactiveForm.reset();
  }

  createForm(): void {
    this.reactiveForm = this.fb.group({
      [DarksiteFormControl.RELATIVE]: ['' as RelativeQuestion, Validators.required],
      [DarksiteFormControl.RELATION]: ['' as RelationQuestion],
      [DarksiteFormControl.OTHER]: [''],
      [DarksiteFormControl.FIRST_NAME]: [
        '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(this.textareaLimit30)],
      ],
      [DarksiteFormControl.LAST_NAME]: [
        '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(this.textareaLimit30)],
      ],
      [DarksiteFormControl.PAX_DOB]: this.fb.group({
        [DOBFormControl.DAY]: [''],
        [DOBFormControl.MONTH]: [''],
        [DOBFormControl.YEAR]: [''],
      }),
      [DarksiteFormControl.PHONE_NUMBER]: ['', [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]],
      [DarksiteFormControl.MOBILE_NUMBER]: ['', [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]],
      [DarksiteFormControl.EMAIL_ADDRESS]: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      [DarksiteFormControl.HOME_ADDRESS]: [''],
      [DarksiteFormControl.PAX_FIRST_NAME]: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      [DarksiteFormControl.PAX_LAST_NAME]: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      [DarksiteFormControl.PAX_NATIONALITY]: [''],
      [DarksiteFormControl.PAX_HOME_ADDRESS]: [''],
      [DarksiteFormControl.COMPANION_NAME]: [''],
    });

    this.formWatcher();
  }

  formWatcher(): void {
    this.subscription.add(
      this.reactiveForm.controls[DarksiteFormControl.RELATIVE].valueChanges.subscribe((value): void => {
        if (value === RelativeQuestion.YES) {
          this.enableField(DarksiteFormControl.RELATION);
          return;
        }

        this.disableField(DarksiteFormControl.RELATION);
      })
    );

    this.subscription.add(
      this.reactiveForm.controls[DarksiteFormControl.RELATION].valueChanges.subscribe((value): void => {
        value === RelationQuestion.OTHER
          ? this.enableField(DarksiteFormControl.OTHER)
          : this.disableField(DarksiteFormControl.OTHER);
      })
    );
  }

  enableField(field: DarksiteFormControl): void {
    this.reactiveForm.controls[field].setValidators([Validators.required]);
    this.reactiveForm.get([field]).reset();
    this.reactiveForm.get([field]).enable();
  }

  disableField(field: DarksiteFormControl): void {
    this.reactiveForm.controls[field].setValidators(null);
    this.reactiveForm.get([field]).reset();
  }

  private relationValue(formData: DarksiteFormData): string {
    if (formData?.[DarksiteFormControl.RELATION] === RelationQuestion.OTHER) {
      return formData?.[DarksiteFormControl.OTHER];
    }

    return formData?.[DarksiteFormControl.RELATION];
  }

  private dateOfBirthValue(formData: DarksiteFormData): string {
    let day = 'dd';
    let month = 'mm';
    let year = 'yyyy';

    if (formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.DAY]) {
      day = formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.DAY];
    }

    if (formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.MONTH]) {
      month = formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.MONTH];
    }

    if (formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.YEAR]) {
      year = formData?.[DarksiteFormControl.PAX_DOB][DOBFormControl.YEAR];
    }

    // 1.mm.yyyy, d.12.yyyy, d.mm.2000. 1.mm.2000, d.12.2000, 1.12.2000
    const dob = `${day}.${month}.${year}`;

    return dob === 'dd.mm.yyyy' ? undefined : dob;
  }

  private isRelative(): string {
    return this.selectedRelative === RelativeQuestion.YES ? 'true' : 'false';
  }

  private evaluateValue(control: string | null | undefined): string | undefined {
    return control ? control : undefined;
  }

  prepareCase(formData: DarksiteFormData): DarksiteFormData {
    return {
      family: this.isRelative(),
      relation: this.relationValue(formData),
      contact: {
        firstName: formData?.[DarksiteFormControl.FIRST_NAME],
        lastName: formData?.[DarksiteFormControl.LAST_NAME],
        phoneNumber: stripSpaces(formData?.[DarksiteFormControl.PHONE_NUMBER]),
        mobileNumber: stripSpaces(formData?.[DarksiteFormControl.MOBILE_NUMBER]),
        emailAddress: this.evaluateValue(formData?.[DarksiteFormControl.EMAIL_ADDRESS]),
        homeAddress: this.evaluateValue(formData?.[DarksiteFormControl.HOME_ADDRESS]),
      },
      passenger: {
        firstName: formData?.[DarksiteFormControl.PAX_FIRST_NAME],
        lastName: formData?.[DarksiteFormControl.PAX_LAST_NAME],
        dateOfBirth: this.dateOfBirthValue(formData),
        nationality: this.evaluateValue(formData?.[DarksiteFormControl.PAX_NATIONALITY]),
        homeAddress: this.evaluateValue(formData?.[DarksiteFormControl.PAX_HOME_ADDRESS]),
      },
      companionTraveller: this.evaluateValue(formData?.[DarksiteFormControl.COMPANION_NAME]),
    };
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();
      return;
    }

    this.loading.set(true);

    this.subscription.add(
      this.incidentUpdatesService
        .createDarksiteCase(this.prepareCase(this.reactiveForm.value))
        .pipe(take(1))
        .subscribe({
          next: (_response: DarksiteCaseResponse): void => {
            this.onSuccess();
          },
          error: (_error: unknown): void => {
            this.onError();
          },
        })
    );
  }

  private onError(): void {
    this.incidentUpdatesService.formEnds.set(false);
    this.loading.set(false);
    this.gtmService.trackElement(
      AnalyticsFormStatus.INCIDENT_UPDATES,
      DARKSITE_GTMCONTEXT,
      ElementTypes.BUTTON,
      'form-submit-error',
      ElementActions.SUBMIT
    );
  }

  private onSuccess(): void {
    this.incidentUpdatesService.formEnds.set(true);
    this.loading.set(false);
    this.reactiveForm.reset();
    this.createForm();
    this.gtmService.trackElement(
      AnalyticsFormStatus.INCIDENT_UPDATES,
      DARKSITE_GTMCONTEXT,
      ElementTypes.BUTTON,
      'form-submit-success',
      ElementActions.SUBMIT
    );
  }
}
